import { useState, useMemo } from "react";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Agent, Category } from "@/types/agent";
import AgentCard from "@/components/agents/AgentCard";
import AppBar from "@/components/common/AppBar";
import PageTransition from "@/components/common/PageTransition";
import { getAiConfig } from "@/pages/api/ai";
import { useRouter } from "next/router";
import { storage } from "@/utils/storage";
import { isInApp, nativeMethod } from "@/utils/bridge";
import Image from "next/image";
import Head from "next/head";
interface AiConfig {
  tabCode: string;
  tabName: string;
  i18nConfigs: Array<{
    name: string;
    description: string;
    welcomeText: string;
    questionList: string[];
    quickCommandList: Array<{
      quickCommand: string;
      quickCommandContent: string;
    }>;
    inputPrompt: string;
    appId: string;
    aiType: number;
    imageUrl: string;
  }>;
}

interface Props {
  aiConfigs: AiConfig[];
}

export default function AgentsPage({ aiConfigs = [] }: Props) {
  const { t } = useTranslation("common");
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState(
    aiConfigs[0]?.tabCode || ""
  );
  const seoTitle = t("home.seo.title");
  const seoDescription = t("home.seo.description");
  const seoKeywords = t("home.seo.keywords");
  // 处理分类点击
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);

    // 找到被点击的分类按钮并滚动到可视区域
    const categoryButton = document.querySelector(
      `[data-category-id="${categoryId}"]`
    );
    if (categoryButton) {
      categoryButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center", // 水平居中
      });
    }

    // 同时滚动列表到顶部
    const listContainer = document.querySelector(".agent-list-container");
    if (listContainer) {
      listContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // 从配置生成分类
  const categories = useMemo(
    () =>
      (aiConfigs || []).map((tab) => ({
        id: tab.tabCode,
        name: tab.tabName,
      })),
    [aiConfigs]
  );

  // 从配置生成 AI 助手列表
  const agents = useMemo(
    () =>
      (aiConfigs || []).flatMap((tab, tabIndex) =>
        (tab.i18nConfigs || []).map((config, configIndex) => ({
          id: `${config.appId}_${tabIndex}_${configIndex}`,
          appId: config.appId,
          name: config.name,
          description: config.description,
          category: tab.tabCode,
          avatar:
            config.imageUrl ||
            `https://placehold.co/400x400/666/fff?text=${config.name}`,
          isOnline: true,
          messageCount: 0,
          welcomeText: config.welcomeText,
          questionList: config.questionList,
          quickCommands: config.quickCommandList,
          aiType: config.aiType,
        }))
      ),
    [aiConfigs]
  );

  // 过滤显示的助手
  const filteredAgents = useMemo(
    () => agents.filter((agent) => agent.category === selectedCategory),
    [selectedCategory, agents]
  );

  // 处理 AI 助手点击
  const handleAgentClick = (agent: any) => {
    // 存储 AI 助手信息
    storage.setAgentInfo(agent.appId, {
      ...agent,
      tabName: categories.find((c) => c.id === agent.category)?.name,
    });
    if (isInApp()) {
      // App 内使用 openWebview 跳转
      nativeMethod.openWebview(`/agents/${agent.appId}`, agent.name);
    } else {
      // 非 App 内使用 router 跳转
      router.push(`/agents/${agent.appId}`);
    }
  };

  const handleClose = () => {
    nativeMethod.closeWebview();
  };

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Head>
      <PageTransition>
        {process.env.NEXT_PUBLIC_HIDEEN_IDX === "true" && (
          <>
            <div className="w-full h-full flex items-center justify-center">
              <Image
                src="https://pgc.otv.cc/iconImg/20250617/1750166266305_logo.png"
                alt="logo"
                style={{ width: "48px", height: "48px%" }}
                width={0}
                height={0}
              />
            </div>
          </>
        )}
        {(!process.env.NEXT_PUBLIC_HIDEEN_IDX ||
          process.env.NEXT_PUBLIC_HIDEEN_IDX !== "true") && (
          <div className="flex flex-col h-full">
            <AppBar
              variant="list"
              title="Ai Center"
              rightIcon={
                <button className="p-2 text-[#333]" onClick={handleClose}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 5L19 19"
                      stroke="#333333"
                      stroke-width="2.5"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M19 5L5 19"
                      stroke="#333333"
                      stroke-width="2.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              }
            />

            {/* Categories */}
            <div className="pt-[16px] pb-[16px]">
              <div className="flex space-x-2 overflow-x-auto scrollbar-hide pe-[16px] ps-[16px]">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    data-category-id={category.id}
                    className={`flex flex-col justify-center items-center px-4 py-2 whitespace-nowrap rounded-[100px] min-w-fit transition-all duration-200 native-click ${
                      selectedCategory === category.id
                        ? "bg-[rgba(22,115,255,0.10)] text-[#1673FF]"
                        : "bg-white text-[#333]"
                    }`}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Agent List */}
            <div className="flex-1 overflow-y-auto native-scroll scroll-bounce agent-list-container">
              <div className="ps-[16px] pe-[16px] pb-[16px] flex flex-col items-center gap-[12px]">
                {filteredAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onClick={() => handleAgentClick(agent)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </PageTransition>
    </>
  );
}

// 替换 getStaticProps 为 getServerSideProps
export const getServerSideProps = async ({ locale }: { locale: string }) => {
  try {
    if (process.env.NEXT_PUBLIC_HIDEEN_IDX === "true") {
      return {
        props: {
          ...(await serverSideTranslations(locale, ["common"])),
          aiConfigs: [],
        },
      };
    } else {
      const result = await getAiConfig();
      // console.log('res: ', JSON.stringify(result, null, 2))
      return {
        props: {
          ...(await serverSideTranslations(locale, ["common"])),
          aiConfigs: result?.aiCenterConfigs || [],
        },
      };
    }
  } catch (error) {
    console.error("Failed to fetch AI config:", error);
    return {
      props: {
        ...(await serverSideTranslations(locale, ["common"])),
        aiConfigs: [],
      },
    };
  }
};
